import { useState } from "react";
import IcomoonIcon from "@icons/IcomoonIcon";
import ToolDetailsDialog from "./ToolDetailsDialog";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

interface ToolExecutionProps {
  toolName: string;
  status: "started" | "completed" | "failed";
  isActive?: boolean;
  toolArguments?: string;
  toolResult?: string;
}

/**
 * Renders a tool execution indicator (started, completed, or failed)
 * Used by both AiProgressUpdates (live) and AiChatSidebar (history)
 */
const ToolExecution = ({
  toolName,
  status,
  isActive = false,
  toolArguments,
  toolResult,
}: ToolExecutionProps) => {
  const [showDialog, setShowDialog] = useState(false);

  // Only show info button when we have arguments or results
  const hasDetails = toolArguments || toolResult;

  const InfoButton = () =>
    hasDetails ? (
      <button
        className="btn btn-link btn-sm p-0 ms-2"
        onClick={(e) => {
          e.stopPropagation();
          setShowDialog(true);
        }}
        title={translate(LanguageKey.view_tool_details_key)}
        style={{ lineHeight: 1 }}
      >
        <IcomoonIcon icon="info" size={14} />
      </button>
    ) : null;

  if (status === "started") {
    return (
      <>
        <div
          className={`p-2 ms-3 mt-2 mb-1 bg-light text-muted rounded small font-monospace d-flex align-items-center ${
            isActive ? "tool-executing" : ""
          }`}
          style={
            isActive
              ? {
                  transition: "all 0.3s ease-in-out",
                  boxShadow: "0 0 15px rgba(25, 135, 84, 0.6)",
                  backgroundColor: "rgba(25, 135, 84, 0.1)",
                  borderLeft: "3px solid #198754",
                }
              : undefined
          }
        >
          <span>
            <strong>🔧</strong> <em>{toolName}</em>
          </span>
          <InfoButton />
        </div>
        <ToolDetailsDialog
          isShow={showDialog}
          onClose={() => setShowDialog(false)}
          toolName={toolName}
          toolArguments={toolArguments}
          toolResult={toolResult}
        />
      </>
    );
  }

  if (status === "completed") {
    return (
      <>
        <div className="p-2 ms-3 mb-2 bg-success bg-opacity-10 text-success rounded small font-monospace d-flex align-items-center">
          <span>
            <strong>✅</strong> <em>{toolName} {translate(LanguageKey.completed_key).toLowerCase()}</em>
          </span>
          <InfoButton />
        </div>
        <ToolDetailsDialog
          isShow={showDialog}
          onClose={() => setShowDialog(false)}
          toolName={toolName}
          toolArguments={toolArguments}
          toolResult={toolResult}
        />
      </>
    );
  }

  // status === "failed"
  return (
    <>
      <div className="p-2 ms-3 bg-danger bg-opacity-10 text-danger rounded small font-monospace mb-2 d-flex align-items-center">
        <span>
          <strong>❌</strong> <em>{toolName} {translate(LanguageKey.failed_key).toLowerCase()}</em>
        </span>
        <InfoButton />
      </div>
      <ToolDetailsDialog
        isShow={showDialog}
        onClose={() => setShowDialog(false)}
        toolName={toolName}
        toolArguments={toolArguments}
        toolResult={toolResult}
      />
    </>
  );
};

export default ToolExecution;
